import { Country } from "../models/country";
import { NavItem } from "../models/navItem";
import { AppUserBase } from "../models/appUserBase";
import { Index } from "../models/index";
import { Industry } from "../models/industry";
import { EntityType } from "../models/enums/entityType.enum";
import { PyramidEntriesByYearDto } from "../models/api/pyramidEntriesByYearDto";
import { PyramidDtoPerYear } from "../models/api/pyramidPerYearDto";
import { PyramidDto } from "../models/api/pyramidDto";
import { PyramidEntitySummary } from "../models/pyramidEntitySummary";
import { PyramidYearPercentage } from "../models/pyramidYearPercentage";
import { CompanyDataCollectionReportCategory } from "../models/enums/companyDataCollectionReportCategory.enum";
import { Category } from "../models/enums/category.enum";

export class Helpers {
  /**
   * Validates if a string is a valid IP address (IPv4 or IPv6)
   * @param ip The IP address string to validate
   * @returns true if the IP is valid, false otherwise
   */
  public static isValidIP(ip: string): boolean {
    if (!ip || typeof ip !== 'string') {
      return false;
    }

    // Trim whitespace
    ip = ip.trim();

    // Check if it's IPv4 or IPv6
    return this.isValidIPv4(ip) || this.isValidIPv6(ip);
  }

  /**
   * Validates if a string is a valid IPv4 address
   * @param ip The IP address string to validate
   * @returns true if the IP is a valid IPv4 address, false otherwise
   */
  public static isValidIPv4(ip: string): boolean {
    if (!ip || typeof ip !== 'string') {
      return false;
    }

    // IPv4 pattern: 4 groups of 1-3 digits separated by dots
    const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
    const match = ip.match(ipv4Regex);

    if (!match) {
      return false;
    }

    // Check each octet
    for (let i = 1; i <= 4; i++) {
      const octet = match[i];

      // Check for leading zeros (except for "0" itself)
      if (octet.length > 1 && octet[0] === '0') {
        return false;
      }

      // Check range (0-255)
      const num = parseInt(octet, 10);
      if (num < 0 || num > 255) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validates if a string is a valid IPv6 address
   * @param ip The IP address string to validate
   * @returns true if the IP is a valid IPv6 address, false otherwise
   */
  public static isValidIPv6(ip: string): boolean {
    if (!ip || typeof ip !== 'string') {
      return false;
    }

    // Handle IPv6 with IPv4 suffix (e.g., ::ffff:192.168.1.1)
    if (ip.includes('.')) {
      const parts = ip.split(':');
      if (parts.length > 0) {
        const lastPart = parts[parts.length - 1];
        if (this.isValidIPv4(lastPart)) {
          // Remove the IPv4 part and validate the IPv6 part
          const ipv6Part = ip.substring(0, ip.lastIndexOf(':') + 1) + '0';
          return this.isValidIPv6Pure(ipv6Part);
        }
      }
    }

    return this.isValidIPv6Pure(ip);
  }

  /**
   * Validates pure IPv6 address (without IPv4 suffix)
   * @param ip The IPv6 address string to validate
   * @returns true if the IP is a valid IPv6 address, false otherwise
   */
  private static isValidIPv6Pure(ip: string): boolean {
    // IPv6 can have :: to represent consecutive zero groups
    const parts = ip.split(':');

    // Check for multiple :: occurrences
    if (ip.split('::').length > 2) {
      return false;
    }

    // If there's a ::, we need to expand it
    if (ip.includes('::')) {
      const [left, right] = ip.split('::');
      const leftParts = left ? left.split(':') : [];
      const rightParts = right ? right.split(':') : [];

      // Total parts should not exceed 8
      if (leftParts.length + rightParts.length >= 8) {
        return false;
      }

      // Validate each part
      const allParts = [...leftParts, ...rightParts];
      return allParts.every(part => this.isValidIPv6Group(part));
    } else {
      // No :: compression, should have exactly 8 groups
      if (parts.length !== 8) {
        return false;
      }

      // Validate each group
      return parts.every(part => this.isValidIPv6Group(part));
    }
  }

  /**
   * Validates a single IPv6 group (hexadecimal, 1-4 characters)
   * @param group The IPv6 group to validate
   * @returns true if the group is valid, false otherwise
   */
  private static isValidIPv6Group(group: string): boolean {
    if (!group || group.length === 0 || group.length > 4) {
      return false;
    }

    // Check if all characters are valid hexadecimal
    const hexRegex = /^[0-9a-fA-F]+$/;
    return hexRegex.test(group);
  }

  /**
   * Converts flat API response with parentId/parentName to hierarchical Industry structure with children
   * @param flatData Array of IndustryFlat items
   * @returns Array of Industry objects with hierarchical children structure
   */
  public static convertFlatToHierarchical(flatData: Array<{ id: string; name: string; parentId: string | null; parentName: string | null }>): Industry[] {
    // Create a map for quick lookup
    const itemMap = new Map<string, Industry>();

    // First pass: Create all Industry objects without children
    flatData.forEach(item => {
      itemMap.set(item.id, {
        id: item.id,
        name: item.name,
        systemReserved: false,
        children: []
      });
    });

    // Result array for root-level industries
    const result: Industry[] = [];

    // Second pass: Build the hierarchy
    flatData.forEach(item => {
      const industryNode = itemMap.get(item.id);
      if (!industryNode) return;

      if (item.parentId === null) {
        // Root level item
        result.push(industryNode);
      } else {
        // Child item - add to parent's children
        const parent = itemMap.get(item.parentId);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(industryNode);
        } else {
          // If parent not found, treat as root level
          result.push(industryNode);
        }
      }
    });

    return result;
  }

  /**
   * Converts hierarchical Industry structure to flat API format with parentId/parentName
   * @param hierarchicalData Array of Industry objects with children
   * @param parentId Optional parent ID for recursive calls
   * @param parentName Optional parent name for recursive calls
   * @returns Array of IndustryFlat objects
   */
  public static convertHierarchicalToFlat(
    hierarchicalData: Industry[],
    parentId: string | null = null,
    parentName: string | null = null
  ): Array<{ id: string; name: string; parentId: string | null; parentName: string | null }> {
    const result: Array<{ id: string; name: string; parentId: string | null; parentName: string | null }> = [];

    hierarchicalData.forEach(item => {
      // Add current item
      result.push({
        id: item.id,
        name: item.name,
        parentId: parentId,
        parentName: parentName
      });

      // Recursively process children
      if (item.children && item.children.length > 0) {
        const childrenFlat = this.convertHierarchicalToFlat(item.children, item.id, item.name);
        result.push(...childrenFlat);
      }
    });

    return result;
  }

  /**
   * Sorts hierarchical Industry tree alphabetically at all levels
   * @param hierarchicalData Array of Industry objects with children
   * @returns Sorted array of Industry objects with sorted children at all levels
   */
  public static sortIndustriesAlphabetically(hierarchicalData: Industry[]): Industry[] {
    // Sort current level alphabetically by name
    const sorted = [...hierarchicalData].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );

    // Recursively sort children
    sorted.forEach(item => {
      if (item.children && item.children.length > 0) {
        item.children = this.sortIndustriesAlphabetically(item.children);
      }
    });

    return sorted;
  }

  /**
   * Converts flat Country API response to hierarchical Country model
   * @param flatData Array of CountryFlat objects from API
   * @returns Array of Country objects with nested related objects
   */
  public static convertCountryFlatToCountry(flatData: any[]): any[] {
    return flatData.map(flat => ({
      id: flat.id,
      name: flat.name,
      code: flat.code || '',
      region: flat.region,
      nationality: flat.nationalityId ? {
        id: flat.nationalityId,
        name: flat.nationalityName || ''
      } : null,
      currency: flat.currencyId ? {
        id: flat.currencyId,
        name: flat.currencyName || ''
      } : null,
      stockExchange1: flat.stockExchange1Id ? {
        id: flat.stockExchange1Id,
        name: flat.stockExchange1Name || ''
      } : null,
      stockExchange2: flat.stockExchange2Id ? {
        id: flat.stockExchange2Id,
        name: flat.stockExchange2Name || ''
      } : null,
      isVisible: flat.isVisible
    }));
  }

  /**
   * Converts hierarchical Country model to flat Country API format
   * @param country Country object
   * @returns CountryFlat object for API
   */
  public static convertCountryToCountryFlat(country: any): any {
    return {
      id: country.id,
      name: country.name,
      code: country.code,
      region: country.region,
      nationalityId: country.nationality?.id || null,
      currencyId: country.currency?.id || null,
      stockExchange1Id: country.stockExchange1?.id || null,
      stockExchange2Id: country.stockExchange2?.id || null,
      isVisible: country.isVisible,
      nationalityName: country.nationality?.name || null,
      currencyName: country.currency?.name || null,
      stockExchange1Name: country.stockExchange1?.name || null,
      stockExchange2Name: country.stockExchange2?.name || null
    };
  }

  /**
   * Accessor for the active business year. Bootstrap installs an accessor
   * backed by ActiveYearService; before bootstrap or in tests, falls back
   * to (calendar year - 1).
   */
  private static activeYearAccessor: () => number = () => new Date().getFullYear() - 1;

  public static setActiveYearAccessor(accessor: () => number): void {
    Helpers.activeYearAccessor = accessor;
  }

  /**
   * Returns the active business year as a string. Sourced from the
   * AppSettings.ActiveYear configuration via ActiveYearService.
   */
  public static defaultMeasurementYear(): string {
    return Helpers.activeYearAccessor().toString();
  }

  /**
   * Generates an array of measurement years from 2007 to the active year, descending.
   */
  public static measurements(): string[] {
    const startYear = Helpers.activeYearAccessor();
    const years: string[] = [];
    for (let year = startYear; year >= 2007; year--) {
      years.push(year.toString());
    }
    return years;
  }

  /**
   * Converts EntityType enum to human-readable text
   * @param entityType The entity type enum value
   * @returns Human-readable text for the entity type
   */
  public static getEntityTypeText(entityType: EntityType): string {
    switch (entityType) {
      case EntityType.LargeShareholder:
        return 'Large Shareholder';
      case EntityType.PyramidalShareholder:
        return 'Pyramidal Shareholder';
      case EntityType.Company:
        return 'Company';
      case EntityType.OpbCompany:
        return 'OPB Company';
      case EntityType.Director:
        return 'Director';
      case EntityType.VoteProposalName:
        return 'Proposal Name';
      default:
        return 'Unknown';
    }
  }

  /**
   * Returns the CSS class for a type-badge based on entity type.
   * Works with both EntityType enum values and string type labels.
   */
  public static getEntityTypeBadgeClass(entityType: EntityType | string): string {
    const typeMap: Record<string, string> = {
      'Large Shareholder': 'type-large-shareholder',
      'Pyramidal Shareholder': 'type-pyramidal',
      'Company': 'type-company',
      'OPB Company': 'type-opb',
      'Director': 'type-director',
      'Proposal Name': 'type-proposal-name',
    };

    if (typeof entityType === 'string') {
      return typeMap[entityType] || '';
    }

    const text = Helpers.getEntityTypeText(entityType);
    return typeMap[text] || '';
  }

  private static readonly tabCategoryMap: Record<string, Category> = {
    'details': Category.None,
    'collection': Category.None,
    'audit1': Category.Audit,
    'audit2': Category.Audit,
    'board': Category.CorporateGovernance,
    'committees': Category.CorporateGovernance,
    'ownership': Category.CorporateGovernance,
    'family': Category.CorporateGovernance,
    'salary1': Category.Compensation,
    'salary2': Category.Compensation,
    'accounting': Category.Audit,
    'society': Category.Csr,
    'employees': Category.Csr,
    'energyWater': Category.Csr,
    'emissionsWaste': Category.Csr,
    'votingResults': Category.VotingResults,
    'process': Category.None,
    'messages': Category.None
  };

  public static getTabCategory(tabKey: string): Category {
    return this.tabCategoryMap[tabKey] ?? Category.None;
  }

  public static getTabsByCategory(category: Category): string[] {
    return Object.entries(this.tabCategoryMap)
      .filter(([_, cat]) => cat === category)
      .map(([key]) => key);
  }

  public static getCountryNameById(countries: Country[], countryId: string): string {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : 'Unknown Country';
  }

  /**
   * Converts pyramid entries grouped by year to a flat list of PyramidDtoPerYear objects
   * @param pyramidsByYear Object where keys are years and values are arrays of pyramid entries
   * @returns Flat array of PyramidDtoPerYear objects, sorted by year (descending) and then by entry
   */
  public static convertPyramidEntriesByYearToList(pyramidsByYear: PyramidEntriesByYearDto): PyramidDtoPerYear[] {
    const result: PyramidDtoPerYear[] = [];

    // Iterate through each year and its pyramids
    Object.keys(pyramidsByYear).forEach(yearKey => {
      const year = parseInt(yearKey, 10);
      const pyramids = pyramidsByYear[yearKey];

      // For each pyramid in this year, create a PyramidDtoPerYear entry
      pyramids.forEach((pyramid: PyramidDto) => {
        result.push({
          year: year,
          pyramid: pyramid
        });
      });
    });

    // Sort by year descending, so most recent years come first
    result.sort((a, b) => b.year - a.year);

    return result;
  }

  /**
   * Converts pyramid entries grouped by year to a list of entity summaries with year-percentage data
   * Groups by unique pyramid entity (company, director, pyramidal shareholder, etc.)
   * @param pyramidsByYear Object where keys are years and values are arrays of pyramid entries
   * @returns Array of PyramidEntitySummary objects, each containing year-percentage pairs
   */
  public static convertPyramidEntriesByYearToEntitySummaries(pyramidsByYear: PyramidEntriesByYearDto): PyramidEntitySummary[] {
    const entityMap = new Map<string, PyramidEntitySummary>();

    // Iterate through each year and its pyramids
    Object.keys(pyramidsByYear).forEach(yearKey => {
      const year = parseInt(yearKey, 10);
      const pyramids = pyramidsByYear[yearKey];

      pyramids.forEach((pyramid: PyramidDto) => {
        // Determine the entity ID, name, and type based on which ID is not null
        let entityId: string;
        let entityName: string;
        let entityType: 'Company' | 'Director' | 'Pyramid Large Shareholder' | 'Pyramidal Shareholder' | 'Opb Company';

        if (pyramid.companyId) {
          entityId = pyramid.companyId;
          entityName = pyramid.companyName;
          entityType = 'Company';
        } else if (pyramid.directorId) {
          entityId = pyramid.directorId;
          entityName = pyramid.directorName;
          entityType = 'Director';
        } else if (pyramid.pyramidLargeShareholderId) {
          entityId = pyramid.pyramidLargeShareholderId;
          entityName = pyramid.pyramidLargeShareholderName;
          entityType = 'Pyramid Large Shareholder';
        } else if (pyramid.pyramidalShareholderId) {
          entityId = pyramid.pyramidalShareholderId;
          entityName = pyramid.pyramidalShareholderName;
          entityType = 'Pyramidal Shareholder';
        } else if (pyramid.opbCompanyId) {
          entityId = pyramid.opbCompanyId;
          entityName = pyramid.opbCompanyName;
          entityType = 'Opb Company';
        } else {
          // Skip entries with no entity ID
          return;
        }

        // Check if this entity already exists in the map
        if (!entityMap.has(entityId)) {
          // Create new entity summary
          entityMap.set(entityId, {
            entityId: entityId,
            entityName: entityName,
            entityType: entityType,
            categoryId: pyramid.categoryId,
            categoryName: pyramid.categoryName,
            subCategoryId: pyramid.subCategoryId,
            subCategoryName: pyramid.subCategoryName,
            yearPercentages: []
          });
        }

        // Add year-percentage to this entity
        const entity = entityMap.get(entityId)!;
        entity.yearPercentages.push({
          year: year,
          percentage: pyramid.percentage
        });
      });
    });

    // Convert map to array and sort year-percentages by year (ascending - oldest to newest)
    const result = Array.from(entityMap.values());
    result.forEach(entity => {
      entity.yearPercentages.sort((a, b) => a.year - b.year);
    });

    // Sort entities by name (handle null values)
    result.sort((a, b) => {
      const nameA = a.entityName || '';
      const nameB = b.entityName || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    return result;
  }

  /**
   * Converts pyramidal shareholder pyramid entries grouped by year to a list of entity summaries with year-percentage data
   * Groups by unique pyramid entity (company, director, pyramidal shareholder, large shareholder, etc.)
   * @param pyramidsByYear Object where keys are years and values are arrays of pyramidal shareholder pyramid entries
   * @returns Array of PyramidEntitySummary objects, each containing year-percentage pairs
   */
  public static convertPyramidalShareholderPyramidsByYearToEntitySummaries(pyramidsByYear: { [year: number]: any[] }): PyramidEntitySummary[] {
    const entityMap = new Map<string, PyramidEntitySummary>();

    // Iterate through each year and its pyramids
    Object.keys(pyramidsByYear).forEach(yearKey => {
      const year = parseInt(yearKey, 10);
      const pyramids = pyramidsByYear[parseInt(yearKey, 10)];

      pyramids.forEach((pyramid: any) => {
        // Determine the entity ID, name, and type based on which ID is not null
        let entityId: string;
        let entityName: string;
        let entityType: 'Company' | 'Director' | 'Pyramid Large Shareholder' | 'Pyramidal Shareholder' | 'Opb Company';

        if (pyramid.companyId) {
          entityId = pyramid.companyId;
          entityName = pyramid.companyName;
          entityType = 'Company';
        } else if (pyramid.directorId) {
          entityId = pyramid.directorId;
          entityName = pyramid.directorName;
          entityType = 'Director';
        } else if (pyramid.pyramidPyramidalShareholderId) {
          entityId = pyramid.pyramidPyramidalShareholderId;
          entityName = pyramid.pyramidPyramidalShareholderName;
          entityType = 'Pyramidal Shareholder';
        } else if (pyramid.largeShareholderId) {
          entityId = pyramid.largeShareholderId;
          entityName = pyramid.largeShareholderName;
          entityType = 'Pyramid Large Shareholder';
        } else if (pyramid.opbCompanyId) {
          entityId = pyramid.opbCompanyId;
          entityName = pyramid.opbCompanyName;
          entityType = 'Opb Company';
        } else {
          // Skip entries with no entity ID
          return;
        }

        // Check if this entity already exists in the map
        if (!entityMap.has(entityId)) {
          // Create new entity summary
          entityMap.set(entityId, {
            entityId: entityId,
            entityName: entityName,
            entityType: entityType,
            categoryId: '',
            categoryName: '',
            subCategoryId: '',
            subCategoryName: '',
            yearPercentages: []
          });
        }

        // Add year-percentage to this entity
        const entity = entityMap.get(entityId)!;
        entity.yearPercentages.push({
          year: year,
          percentage: pyramid.percentage
        });
      });
    });

    // Convert map to array and sort year-percentages by year (ascending - oldest to newest)
    const result = Array.from(entityMap.values());
    result.forEach(entity => {
      entity.yearPercentages.sort((a, b) => a.year - b.year);
    });

    // Sort entities by name (handle null values)
    result.sort((a, b) => {
      const nameA = a.entityName || '';
      const nameB = b.entityName || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    return result;
  }

  /**
   * Generates a gradient color palette based on a base color
   * @param baseColor The base color in hex or rgb format
   * @param count The number of colors to generate
   * @returns Array of color strings in HSL format
   */
  public static generateGradientPalette(baseColor: string, count: number): string[] {
    // Convert the base color to HSL
    const hsl = this.rgbToHsl(this.parseColorToRgb(baseColor));
    const colors: string[] = [];

    if (count === 1) {
      return [baseColor];
    }

    // Generate gradient from lighter to darker shades of the secondary color
    for (let i = 0; i < count; i++) {
      const factor = i / (count - 1); // 0 to 1
      // Vary lightness from 70% (lighter) to 30% (darker)
      const lightness = 70 - (factor * 40);
      // Slightly vary saturation for more depth (60% to 80%)
      const saturation = hsl.s * 100 + (factor * 20);

      colors.push(`hsl(${hsl.h}, ${Math.min(100, saturation)}%, ${lightness}%)`);
    }

    return colors;
  }

  /**
   * Parses a color string to RGB values
   * @param color Color string in hex (#RRGGBB) or rgb/rgba format
   * @returns Object with r, g, b values (0-255)
   */
  public static parseColorToRgb(color: string): { r: number; g: number; b: number } {
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.substring(1);
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b };
    }

    // Handle rgb/rgba colors
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1]),
        g: parseInt(rgbMatch[2]),
        b: parseInt(rgbMatch[3])
      };
    }

    // Default fallback
    return { r: 103, g: 80, b: 164 }; // #6750A4
  }

  /**
   * Converts RGB values to HSL values
   * @param rgb Object with r, g, b values (0-255)
   * @returns Object with h (0-360), s (0-1), l (0-1) values
   */
  public static rgbToHsl(rgb: { r: number; g: number; b: number }): { h: number; s: number; l: number } {
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: s,
      l: l
    };
  }

  /**
   * Parses API error responses and extracts user-friendly error messages.
   * Handles two error formats:
   * 1. ASP.NET Core validation errors: { errors: { field: [messages] }, title: "...", status: 400 }
   * 2. Custom error responses: { error: "message" }
   *
   * @param error The HTTP error response object
   * @returns A formatted error message string, or 'Unknown error' if no error message found
   */
  public static parseApiError(error: any): string {
    if (!error) {
      return 'Unknown error';
    }

    // Check for error.error (the actual response body)
    const errorBody = error.error;
    if (!errorBody) {
      return error.message || 'Unknown error';
    }

    // Format 1: ASP.NET Core validation errors with errors object
    if (errorBody.errors && typeof errorBody.errors === 'object') {
      const errorMessages: string[] = [];

      // Iterate through each field that has validation errors
      Object.keys(errorBody.errors).forEach(field => {
        const fieldErrors = errorBody.errors[field];

        if (Array.isArray(fieldErrors)) {
          // Add each error message for this field
          fieldErrors.forEach(message => {
            // Format: "FieldName: error message"
            const formattedField = this.formatFieldName(field);
            errorMessages.push(`<strong>${formattedField}:</strong> ${message}`);
          });
        }
      });

      // Return all validation errors joined with line breaks
      if (errorMessages.length > 0) {
        return errorMessages.join('</br>');
      }

      // If we have an errors object but no messages, return the title if available
      return errorBody.title || 'Validation error occurred';
    }

    // Format 2: Custom error with single error message
    if (errorBody.error && typeof errorBody.error === 'string') {
      return errorBody.error;
    }

    // Fallback: check for common error properties
    if (errorBody.message) {
      return errorBody.message;
    }

    if (errorBody.title) {
      return errorBody.title;
    }

    // Last resort: try to get status text or generic message
    return error.statusText || error.message || 'Unknown error';
  }

  public static getReportUrlLabel(category: CompanyDataCollectionReportCategory, urlType: number): string {
    if (category === CompanyDataCollectionReportCategory.CorporateGovernance) {
      switch (urlType) {
        case 1: return 'Link To Annual Report';
        case 2: return '2nd Link';
        case 3: return 'Link To C.G.Report';
      }
    } else if (category === CompanyDataCollectionReportCategory.Compensation) {
      switch (urlType) {
        case 1: return 'Link to Remuneration Report';
      }
    } else if (category === CompanyDataCollectionReportCategory.Audit) {
      switch (urlType) {
        case 1: return 'Link for Audit';
      }
    } else if (category === CompanyDataCollectionReportCategory.Csr) {
      switch (urlType) {
        case 1: return 'Link to ESG Report';
      }
    } else if (category === CompanyDataCollectionReportCategory.VotingResults) {
      switch (urlType) {
        case 1: return 'Link to Voting Results';
      }
    }
    return 'URL';
  }

  /**
   * Converts an ISO date string to display format.
   * If the date is Jan 1st, shows year only. Otherwise shows DD/MM/YYYY.
   */
  public static toDisplayDate(dateStr?: any): string {
    if (!dateStr) return '';
    const str = typeof dateStr === 'string' ? dateStr : String(dateStr);
    if (str.startsWith('0001') || str.startsWith('1900')) return '';
    if (/^\d{4}$/.test(str)) return str;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) return str;
    const match = str.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, year, month, day] = match;
    if (day === '01' && month === '01') return year;
    return `${day}/${month}/${year}`;
  }

  /**
   * Converts a display date (DD/MM/YYYY or YYYY) to ISO format (YYYY-MM-DDT00:00:00Z).
   */
  public static toIsoDate(displayDate: string): string {
    if (!displayDate) return '';
    if (/^\d{4}$/.test(displayDate)) return `${displayDate}-01-01T00:00:00Z`;
    const parts = displayDate.split('/');
    if (parts.length !== 3 || parts[2].length !== 4) return '';
    const [day, month, year] = parts;
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  /**
   * Handles focus on a date input: expands year-only value to DD/MM/YYYY.
   */
  public static onDateInputFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (/^\d{4}$/.test(input.value)) {
      input.value = `01/01/${input.value}`;
    }
  }

  /**
   * Handles blur on a date input: collapses 01/01/YYYY back to year-only.
   */
  public static onDateInputBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (/^01\/01\/\d{4}$/.test(input.value)) {
      input.value = input.value.substring(6);
    }
  }

  /**
   * Handles input on a date text field: strips non-date chars and auto-inserts slashes.
   * Returns the sanitized value.
   */
  public static onDateInputFormat(event: Event): string {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9/]/g, '');

    if (value.length === 2 && !value.includes('/')) {
      value += '/';
    } else if (value.length === 5 && value.indexOf('/', 3) === -1) {
      value += '/';
    }

    input.value = value;
    return value;
  }

  private static formatFieldName(fieldName: string): string {
    if (!fieldName) {
      return fieldName;
    }

    // Handle special cases like "Id" -> "ID"
    let formatted = fieldName
      .replace(/Id$/, 'ID')  // Replace trailing "Id" with "ID"
      .replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space before capital letters
      .replace(/^./, str => str.toUpperCase());  // Capitalize first letter

    return formatted;
  }
}
